"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.weeksInMonth = exports.getWeekTimeframe = exports.getCurrentWeek = exports.getWeekForDate = exports.getWeeks = exports.getStartOfWeekForDate = exports.getPrevSunday = exports.daysInCashflowPeriod = exports.daysInWeek = exports.isInLastWeekOfMonth = exports.isWithinCashflowEndRange = exports.isWithinCashflowStartRange = exports.getEndOfCashflowDate = exports.getStartOfCashflowMonthByDate = exports.getStartOfCashflowDate = exports.getCashflowMonthFromMonthsAgo = exports.getCashflowMonth = exports.getCurrentCashflowMonth = exports.localizedMoment = void 0;
const lodash_1 = require("lodash");
const moment = require("moment");
const assert = require("assert");
const consts_1 = require("./consts");
function localizedMoment(locale, date = new Date()) {
    if (locale) {
        return moment(date).locale(locale);
    }
    return moment(date);
}
exports.localizedMoment = localizedMoment;
function getCurrentCashflowMonth(cashflowStartDay) {
    return getCashflowMonth({ date: new Date(), cashflowStartDay });
}
exports.getCurrentCashflowMonth = getCurrentCashflowMonth;
function getCashflowMonth({ date, cashflowStartDay }) {
    const dateMoment = moment(date);
    return (dateMoment.date() < cashflowStartDay)
        ? dateMoment.clone().subtract(1, 'month').format(consts_1.CASHFLOW_DATE_FORMAT)
        : dateMoment.format(consts_1.CASHFLOW_DATE_FORMAT);
}
exports.getCashflowMonth = getCashflowMonth;
function getCashflowMonthFromMonthsAgo(cashflowMonth, previousMonths) {
    return moment(cashflowMonth).subtract(previousMonths, 'month').format(consts_1.CASHFLOW_DATE_FORMAT);
}
exports.getCashflowMonthFromMonthsAgo = getCashflowMonthFromMonthsAgo;
function getStartOfCashflowDate({ cashflowMonth, cashflowStartDay, }) {
    return moment(cashflowMonth).date(cashflowStartDay).toDate();
}
exports.getStartOfCashflowDate = getStartOfCashflowDate;
function getStartOfCashflowMonthByDate({ date, cashflowStartDay }) {
    return getStartOfCashflowDate({ cashflowMonth: getCashflowMonth({ date, cashflowStartDay }), cashflowStartDay });
}
exports.getStartOfCashflowMonthByDate = getStartOfCashflowMonthByDate;
function getEndOfCashflowDate({ cashflowMonth, cashflowStartDay, }) {
    return moment(cashflowMonth)
        .date(cashflowStartDay)
        .add(1, 'months')
        .subtract(1, 'days')
        .toDate();
}
exports.getEndOfCashflowDate = getEndOfCashflowDate;
function isWithinCashflowStartRange({ date, cashflowMonth, cashflowStartDay, daysFromStart }) {
    const momentDate = moment(date);
    const firstDayOfCashflow = _getFirstDayOfCashflowDate(cashflowMonth, cashflowStartDay);
    const isAfterStartOfCashflow = momentDate.isSameOrAfter(firstDayOfCashflow);
    const isWithinStartRange = firstDayOfCashflow.clone().add(daysFromStart, 'days').isAfter(momentDate);
    return isAfterStartOfCashflow && isWithinStartRange;
}
exports.isWithinCashflowStartRange = isWithinCashflowStartRange;
function isWithinCashflowEndRange({ date, cashflowMonth, cashflowStartDay, daysFromEnd }) {
    const momentDate = moment(date);
    const lastDayOfCashflow = _getLastDayOfCashflowDate(cashflowMonth, cashflowStartDay);
    const isBeforeEndOfCashflow = momentDate.isSameOrBefore(lastDayOfCashflow);
    const isWithinEndRange = lastDayOfCashflow.clone().subtract(daysFromEnd, 'days').isBefore(momentDate);
    return isBeforeEndOfCashflow && isWithinEndRange;
}
exports.isWithinCashflowEndRange = isWithinCashflowEndRange;
function isInLastWeekOfMonth({ now, cashflowStartDay, locale }) {
    const weeks = getWeeks(getCashflowMonth({ date: now, cashflowStartDay }), cashflowStartDay, locale);
    return getWeekForDate(new Date(now), weeks) === weeks[weeks.length - 1];
}
exports.isInLastWeekOfMonth = isInLastWeekOfMonth;
function daysInWeek(weekIndex, cashflowMonth, cashflowStartDayInMonth, locale) {
    if (_isFirstWeekOfMonth(weekIndex)) {
        return 7 - localizedMoment(locale, moment(cashflowMonth).date(cashflowStartDayInMonth)).weekday();
    }
    if (_isLastWeekOfMonth({ weekIndex, cashflowMonth, cashflowStartDayInMonth, locale })) {
        const lastDayOfCashflow = _getLastDayOfCashflowDate(cashflowMonth, cashflowStartDayInMonth);
        return localizedMoment(locale, lastDayOfCashflow).weekday() + 1;
    }
    return 7;
}
exports.daysInWeek = daysInWeek;
function daysInCashflowPeriod(cashflowMonth) {
    // even when the cashflow starts on a day other than the 1st
    // the number of days in the cashflow is the same as the days of the month it started at.
    return moment(cashflowMonth).daysInMonth();
}
exports.daysInCashflowPeriod = daysInCashflowPeriod;
function getPrevSunday(date) {
    return getStartOfWeekForDate(date, consts_1.DateLocales.EN_IL);
}
exports.getPrevSunday = getPrevSunday;
function getStartOfWeekForDate(date, locale) {
    const dateMoment = localizedMoment(locale, date);
    return dateMoment.startOf('week').toDate();
}
exports.getStartOfWeekForDate = getStartOfWeekForDate;
function getWeeks(cashflowMonth, cashflowStartDay, locale) {
    return (0, lodash_1.times)(weeksInMonth(cashflowMonth, cashflowStartDay, locale), weekIndex => _getWeek({ weekIndex, cashflowMonth, cashflowStartDay, locale }));
}
exports.getWeeks = getWeeks;
function getWeekForDate(date, weeks) {
    return weeks.find(({ startDate, endDate }) => startDate <= date && date < endDate);
}
exports.getWeekForDate = getWeekForDate;
function getCurrentWeek(cashflowStartDay, locale) {
    const weeks = getWeeks(getCurrentCashflowMonth(cashflowStartDay), cashflowStartDay, locale);
    const currentWeek = weeks.find(week => getWeekTimeframe({ referenceDate: new Date(), week }) === consts_1.WeekTimeFrame.Current);
    assert.ok(currentWeek, 'Current week not found');
    return currentWeek;
}
exports.getCurrentWeek = getCurrentWeek;
function _getWeek({ weekIndex, cashflowMonth, cashflowStartDay, locale, }) {
    const startDate = weekIndex === 0 ? _getFirstDayOfCashflowDate(cashflowMonth, cashflowStartDay)
        : localizedMoment(locale, _getFirstDayOfCashflowDate(cashflowMonth, cashflowStartDay).add(weekIndex, 'weeks'))
            .startOf('week');
    const days = daysInWeek(weekIndex, cashflowMonth, cashflowStartDay, locale);
    const endDate = startDate.clone().add(days, 'days').toDate();
    return {
        index: weekIndex,
        startDate: startDate.toDate(),
        endDate,
        days,
    };
}
function getWeekTimeframe({ referenceDate, week, }) {
    if (referenceDate < week.startDate) {
        return consts_1.WeekTimeFrame.Future;
    }
    if (referenceDate < week.endDate) {
        return consts_1.WeekTimeFrame.Current;
    }
    return consts_1.WeekTimeFrame.Past;
}
exports.getWeekTimeframe = getWeekTimeframe;
function weeksInMonth(cashflowMonth, cashflowStartDay, locale) {
    const firstDayOfCashflowDate = _getFirstDayOfCashflowDate(cashflowMonth, cashflowStartDay);
    const lastDayOfCashflowDate = _getLastDayOfCashflowDate(cashflowMonth, cashflowStartDay);
    const firstStartOfWeek = getStartOfWeekForDate(firstDayOfCashflowDate.toDate(), locale);
    const lastStartOfWeek = getStartOfWeekForDate(lastDayOfCashflowDate.toDate(), locale);
    return _getDiffInWeeks(moment(firstStartOfWeek), moment(lastStartOfWeek)) + 1;
}
exports.weeksInMonth = weeksInMonth;
function _getFirstDayOfCashflowDate(cashflowMonth, cashflowStartDay) {
    return moment(cashflowMonth).startOf('month').date(cashflowStartDay);
}
function _getLastDayOfCashflowDate(cashflowMonth, cashflowStartDay) {
    return moment(cashflowMonth)
        .startOf('month')
        .add(1, 'month')
        .date(cashflowStartDay)
        .subtract(1, 'day');
}
function _isFirstWeekOfMonth(weekIndex) {
    return weekIndex === 0;
}
function _isLastWeekOfMonth({ weekIndex, cashflowMonth, cashflowStartDayInMonth, locale, }) {
    return weekIndex === weeksInMonth(cashflowMonth, cashflowStartDayInMonth, locale) - 1;
}
function _getDiffInWeeks(firstDate, secondDate) {
    return secondDate.diff(firstDate, 'week');
}
